<template>
  <v-container fluid class="pa-2 pos-root">
    <v-row class="pos-row" no-gutters>
      <!-- Columna Carrito -->
      <v-col cols="12" md="4" class="pos-col cart-col pa-2">
        <v-card class="cart-section elevation-2" outlined>
          <!-- Header acciones -->
          <v-toolbar dense flat color="primary" dark class="cart-header">
            <v-spacer></v-spacer>

            <!-- Scanner -->
            <v-spacer></v-spacer>
            <v-chip color="white" class="primary--text">
              <v-icon left small color="primary">mdi-package-variant</v-icon
              >{{ totals.qty }} ítems
            </v-chip>
          </v-toolbar>

          <!-- Tabla Carrito -->
          <div class="cart-table-wrapper pa-3">
            <div ref="cartContainer" class="cart-items-container">
              <v-list v-if="cart.length > 0" class="cart-list py-0">
                <template v-for="(item, index) in cart">
                  <v-list-item :key="item.ean" class="cart-item-row">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium mb-1">
                        {{ item.nombre }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption grey--text">
                        {{ item.refe }} {{ item.color }} {{ item.talla }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        v-if="item.note"
                        class="text-caption orange--text mt-1"
                      >
                        <v-icon x-small class="mr-1">mdi-note</v-icon>{{ item.note }}
                      </v-list-item-subtitle>

                      <div class="d-flex align-center mt-2">
                        <span class="mx-2 font-weight-medium"
                          >{{ item.cantidad }} {{ item.uom || "UN" }}</span
                        >
                      </div>
                    </v-list-item-content>

                    <v-list-item-action class="align-self-start mt-2">
                      <div class="text-right">
                        <div class="font-weight-bold primary--text">
                          {{ formatCurrency(item.precio * item.cantidad) }}
                        </div>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider
                    v-if="index < cart.length - 1"
                    :key="`divider-${item.ean}`"
                  ></v-divider>
                </template>
              </v-list>

              <!-- Empty state -->
              <div v-else class="text-center py-6 grey--text">
                <v-icon large color="grey lighten-1">mdi-cart-outline</v-icon>
                <div class="mt-2">Escanee o use el panel de la derecha</div>
              </div>
            </div>
          </div>
          <v-card flat class="mt-2 totals-card pa-3 elevation-3" color="grey lighten-5">
            <!-- Totales / Pago -->
            <v-card-text class="py-1">
              <v-row dense>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Subtotal</div>
                  <div class="font-weight-medium">{{ formatCurrency(subtotal) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Impuestos</div>
                  <div class="font-weight-medium">{{ formatCurrency(totalTax) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Pagado</div>
                  <div class="font-weight-medium success--text">
                    {{ formatCurrency(totals.paid) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text">Pendiente</div>
                  <div
                    class="font-weight-bold"
                    :class="totals.unpaid === 0 ? 'success--text' : 'orange--text'"
                  >
                    {{ formatCurrency(totals.unpaid) }}
                  </div>
                </v-col>
              </v-row>

              <div v-if="taxBreakdown.length" class="tax-breakdown mt-1">
                <div
                  v-for="t in taxBreakdown"
                  :key="t.rate"
                  class="d-flex justify-space-between text-caption"
                >
                  <span>IVA {{ t.rate }}% (Base {{ formatCurrency(t.base) }})</span>
                  <span>{{ formatCurrency(t.tax) }}</span>
                </div>
              </div>
              <v-row dense>
                <!-- NUEVO: botón guardar sin pago -->
                <v-col cols="12">
                  <v-btn
                    block
                    color="info"
                    dark
                    :disabled="!canProcessNoPay"
                    @click="checkoutDialog = true"
                  >
                    <v-icon left small>mdi-content-save</v-icon>
                    Con Pago
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card>
      </v-col>

      <!-- Columna Panel Productos -->
      <v-col cols="12" md="8" class="pos-col pa-2">
        <v-card class="product-section elevation-2" outlined>
          <div class="product-panel pa-3">
            <InvoiceScanner
              ref="scanner"
              @code-scanned="handleBarcodeScanned"
              @show-message="showMessage"
              class="mb-3"
            />

            <!-- Tabs Herramientas -->

            <v-tabs-items v-model="leftTab" class="pad-items">
              <v-tab-item class="pad-item-wrapper">
                <ProductPad
                  :cartItemTotal="totals.unpaid"
                  @product-selected="add2cart"
                  @fastPayment="fastPayment"
                />
              </v-tab-item>
              <v-tab-item class="pad-item-wrapper">
                <PLU
                  :cartItemTotal="totals.unpaid"
                  @product-selected="add2cart"
                  @fastPayment="fastPayment"
                />
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Checkout -->
    <CheckoutComponent
      v-if="checkoutDialog"
      :visible.sync="checkoutDialog"
      :totalAmount="totals.total"
      :unpaidAmount="totals.unpaid"
      :vendors="vendors"
      :paymentMethods="FP"
      :idList="dynamicSettings.documentTypes"
      :municipalities="[]"
      :companyData="company"
      :clientData="client"
      :validations="checkoutValidations"
      @process-payment="processPayment"
      @show-message="showMessage"
      @open-client-dialog="openClientDialog"
    />

    <!-- Diálogo para ingresar cantidad con teclado numérico -->
    <v-dialog
      v-model="qtyDialog"
      max-width="400"
      persistent
      @keydown.enter="confirmQtyInput"
      @keydown.esc="cancelQtyInput"
    >
      <v-card>
        <v-card-title class="primary white--text" v-if="pendingProduct">
          <v-icon left color="white">mdi-scale</v-icon>
          {{ pendingProduct.nombre }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            ref="qtyInputField"
            v-model="qtyInput"
            outlined
            class="text-h4 text-center pt-4 qty-display"
            hide-details
            background-color="grey lighten-4"
            autofocus
            @input="validateQtyInput"
            @keydown="handleKeydown"
          >
            <template v-slot:append>
              <span class="text-subtitle-1 grey--text">kg/und</span>
            </template>
          </v-text-field>

          <!-- Teclado Numérico -->
          <v-container fluid class="pa-0 mt-3">
            <v-row dense>
              <v-col cols="4" v-for="num in [7, 8, 9]" :key="num">
                <v-btn
                  large
                  block
                  color="grey lighten-2"
                  @click="addToQtyInput(num.toString())"
                >
                  <span class="text-h5">{{ num }}</span>
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="4" v-for="num in [4, 5, 6]" :key="num">
                <v-btn
                  large
                  block
                  color="grey lighten-2"
                  @click="addToQtyInput(num.toString())"
                >
                  <span class="text-h5">{{ num }}</span>
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="4" v-for="num in [1, 2, 3]" :key="num">
                <v-btn
                  large
                  block
                  color="grey lighten-2"
                  @click="addToQtyInput(num.toString())"
                >
                  <span class="text-h5">{{ num }}</span>
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="4">
                <v-btn large block color="grey lighten-2" @click="addToQtyInput('.')">
                  <span class="text-h5">.</span>
                </v-btn>
              </v-col>
              <v-col cols="4">
                <v-btn large block color="grey lighten-2" @click="addToQtyInput('0')">
                  <span class="text-h5">0</span>
                </v-btn>
              </v-col>
              <v-col cols="4">
                <v-btn large block color="error" @click="clearQtyInput">
                  <v-icon>mdi-backspace</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelQtyInput">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" large :disabled="!isValidQty" @click="confirmQtyInput">
            <v-icon left>mdi-check</v-icon>
            Agregar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Visualizador de documentos -->

    <docViewer
      :item="item"
      :dialog="dialog"
      :newInvoice="nInvoice"
      @close="dialog = false"
      @refresh="refresh_view()"
    />

    <!-- (Opcional) Dialog Cliente futuro -->
  </v-container>
</template>

<script>
import { formatCurrency, webserver } from "@/services/webserver.js";
import ProductPad from "@/views/pos/ProductPadQ.vue";
import PLU from "@/views/pos/PLUD.vue";
import InvoiceScanner from "./components/InvoiceScanner.vue";
import CheckoutComponent from "@/components/checkout.vue";
import createPayment from "@/utils/createPayment.js";
import createDoc from "@/utils/create_doc.js";
import createCompany from "@/utils/createCompany.js";
import docViewer from "@/components/doc_viewer.vue";
import countryUtils from "@/utils/countryUtils.js";

export default {
  name: "InvoiceQ",
  components: {
    ProductPad,
    PLU,
    InvoiceScanner,
    CheckoutComponent,
    docViewer,
  },
  data() {
    return {
      leftTab: 0,
      cart: [],
      payments: [],
      totals: { qty: 0, total: 0, paid: 0, unpaid: 0 },
      qtyDialog: false,
      qtyInput: "",
      pendingProduct: null,
      client: {
        PartyName: "CONSUMIDOR FINAL",
        PartyIdentification: "222222222222",
        AddressLine: "",
        client_email: "",
        client_phone: "",
        CityName: "",
        typeid: "CC",
      },
      checkoutDialog: false,
      FP: (window.settings && window.settings.FP) || [],
      vendors: [],
      vendor: null,
      company: createCompany(),
      payment: createPayment(),
      documentType: "FE", // Factura Electrónica por defecto para Colombia
      processing: false,
      dialog: false,
      item: createDoc(),
      nInvoice: true,
      FP_DEFAULT: { codigo: "01", valor: "EFECTIVO" },
      dynamicSettings: {
        documentTypes: [],
        departments: [],
        liabilityTypes: [],
        organizationTypes: [],
        regimeTypes: [],
        vatRates: [],
      },
      clientesGenericos: {
        contado: {
          PartyIdentification: "99999999R",
          PartyName: "CLIENTE CONTADO",
          typeid: "DNI",
          AddressLine: "Venta mostrador",
          client_email: "",
          client_phone: "",
        },
        consumidor: {
          PartyIdentification: "222222222222",
          PartyName: "CONSUMIDOR FINAL",
          typeid: "CC",
          AddressLine: "Consumidor Final",
          client_email: "",
          client_phone: "",
        },
      },
      checkoutValidations: [
        // NUEVO: estructura completa para evitar undefined
        { key: "client", status: null, label: "Nombre del cliente requerido" },
        { key: "email", status: true, label: "Correo electrónico es opcional" },
        { key: "document", status: null, label: "Documento de identificación requerido" },
        { key: "doctype", status: null, label: "Tipo de documento requerido" },
        { key: "vendor", status: null, label: "Vendedor requerido" },
        { key: "electronic", status: false, label: "Requiere Factura Electrónica" },
        { key: "value", status: false, label: "Valor" },
        { key: "city", status: null, label: "Ciudad requerida" },
      ],
    };
  },
  computed: {
    subtotal() {
      return this.cart.reduce((s, i) => s + i.precio * i.cantidad, 0);
    },
    totalTax() {
      return this.cart.reduce((s, item) => {
        const { taxUnit } = this.computeItemAmounts(item);
        return s + taxUnit * item.cantidad;
      }, 0);
    },
    taxBreakdown() {
      const map = {};
      this.cart.forEach((item) => {
        const rate = parseFloat(item.tax) || 0;
        const { baseUnit, taxUnit } = this.computeItemAmounts(item);
        if (!map[rate]) map[rate] = { rate, base: 0, tax: 0 };
        map[rate].base += baseUnit * item.cantidad;
        map[rate].tax += taxUnit * item.cantidad;
      });
      return Object.values(map)
        .filter((r) => r.tax > 0.0001)
        .sort((a, b) => b.rate - a.rate);
    },
    canProcess() {
      return this.cart.length > 0 && this.totals.unpaid === 0 && !this.processing;
    },
    // NUEVO: permitir guardar aun con saldo pendiente
    canProcessNoPay() {
      return this.cart.length > 0 && !this.processing;
    },
    isValidQty() {
      const qty = parseFloat(this.qtyInput);
      return !isNaN(qty) && qty > 0;
    },
  },
  mounted() {
    this.loadVendors();
    this.loadDynamicSettings();
    this.company = window.seller || this.company;
  },
  watch: {
    // ✅ RESETEAR completamente cuando se cierra el doc_viewer
    dialog(newVal) {
      if (!newVal) {
        this.reset(); // ✅ Ahora puede resetear el item porque dialog está cerrado
      }
    },
    qtyDialog(newVal) {
      if (newVal) {
        // Enfocar el campo cuando se abre el diálogo
        this.$nextTick(() => {
          if (this.$refs.qtyInputField) {
            this.$refs.qtyInputField.focus();
            // Seleccionar todo el texto para facilitar el reemplazo
            this.$refs.qtyInputField.$el.querySelector("input").select();
          }
        });
      }
    },
  },
  methods: {
    refresh_view() {
      var e = this.item;
      var issudate = e.issuedate.split(" ");
      var doc = {
        store: e.store,
        issueDate: issudate[0],
        did: e.doc_number,
      };
      this.loading_status = true;
      webserver("get_invoice_fe", doc, (data) => {
        this.loading_status = false;
        this.item = data;
        this.dialog = true;
      });
    },
    formatCurrency,
    loadVendors() {
      webserver("vendors", { store: window.store.store_id }, (data) => {
        this.vendors = data;
        if (data[0]) this.vendor = data[0].codigo;
      });
    },
    loadDynamicSettings() {
      try {
        console.log("🌍 Cargando configuraciones dinámicas para InvoicePOS...");

        // Cargar configuraciones específicas por país
        this.dynamicSettings = {
          documentTypes: countryUtils.getDocumentTypes(),
          departments: countryUtils.getDepartments(),
          liabilityTypes: countryUtils.getLiabilityTypes(),
          organizationTypes: countryUtils.getOrganizationTypes(),
          regimeTypes: countryUtils.getRegimeTypes(),
          vatRates: countryUtils.getVatRates(),
        };

        // 🇨🇴 COLOMBIA: Actualizar clientes genéricos según el país
        const countryCode = countryUtils.getCurrentCountryCode();
        if (countryCode === "CO") {
          this.clientesGenericos = {
            contado: {
              PartyIdentification: "222222222222",
              PartyName: "CLIENTE CONTADO",
              typeid: "CC",
              AddressLine: "Venta mostrador",
              client_email: "",
              client_phone: "",
            },
            consumidor: {
              PartyIdentification: "222222222222",
              PartyName: "CONSUMIDOR FINAL",
              typeid: "CC",
              AddressLine: "Consumidor Final",
              client_email: "",
              client_phone: "",
            },
          };
        }

        console.log("✅ Configuraciones dinámicas cargadas:", {
          country: countryUtils.getCurrentCountryCode(),
          documentsCount: this.dynamicSettings.documentTypes.length,
          departmentsCount: this.dynamicSettings.departments.length,
        });
      } catch (error) {
        console.error("❌ Error cargando configuraciones dinámicas:", error);
        this.showMessage({
          text: "Error cargando configuraciones del país",
          color: "error",
          timeout: 3000,
        });
      }
    },
    handleBarcodeScanned(code) {
      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          { field: "id_pos", operator: "=", value: window.store.store_id },
          { field: "ean", operator: "=", value: code },
        ],
      };
      webserver("get_catalog", qry, (data) => {
        if (data[0]) this.add2cart(data[0]);
        else this.showMessage({ text: "Producto no encontrado", color: "error" });
      });
    },
    showMessage(msg) {
      this.$store && this.$store.commit("setSnackbar", msg);
    },
    add2cart(raw) {
      console.log("[POS] add2cart -> RAW INPUT", raw);
      // Interceptar para mostrar diálogo de cantidad
      this.pendingProduct = raw;
      this.qtyInput = "1";
      this.qtyDialog = true;
    },
    confirmQtyInput() {
      if (!this.isValidQty) return;
      const qty = parseFloat(this.qtyInput);
      this.qtyDialog = false;
      this.addToCartWithQty(this.pendingProduct, qty);
      this.pendingProduct = null;
      this.qtyInput = "";
    },
    cancelQtyInput() {
      this.qtyDialog = false;
      this.pendingProduct = null;
      this.qtyInput = "";
    },
    addToQtyInput(value) {
      // Evitar múltiples puntos decimales
      if (value === "." && this.qtyInput.includes(".")) return;
      // Limitar decimales a 3 dígitos
      if (this.qtyInput.includes(".") && this.qtyInput.split(".")[1].length >= 3) return;
      this.qtyInput += value;
    },
    clearQtyInput() {
      if (this.qtyInput.length > 0) {
        this.qtyInput = this.qtyInput.slice(0, -1);
      }
    },
    validateQtyInput(value) {
      // Permitir solo números y un punto decimal
      const sanitized = value.replace(/[^0-9.]/g, "");

      // Evitar múltiples puntos
      const parts = sanitized.split(".");
      if (parts.length > 2) {
        this.qtyInput = parts[0] + "." + parts.slice(1).join("");
        return;
      }

      // Limitar decimales a 3 dígitos
      if (parts.length === 2 && parts[1].length > 3) {
        this.qtyInput = parts[0] + "." + parts[1].substring(0, 3);
        return;
      }

      this.qtyInput = sanitized;
    },
    handleKeydown(event) {
      // Permitir teclas especiales
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter",
        "Escape",
      ];
      if (allowedKeys.includes(event.key)) return;

      // Permitir números y punto decimal
      if (!/[0-9.]/.test(event.key)) {
        event.preventDefault();
      }
    },
    addToCartWithQty(raw, quantity) {
      // Clonar para no mutar el objeto fuente
      const itm = { ...raw };
      const rawSnapshot = JSON.parse(JSON.stringify(itm));
      // Normalizar precio bruto
      itm.precio = parseFloat(itm.precio || itm.price || 0);
      itm.cantidad = quantity || 1;
      itm.note = itm.note || "";
      // Preservar explícitamente el campo uom (unidad de medida)
      // Normalizar: si viene vacío, null o undefined, usar "UN" por defecto
      itm.uom = (itm.uom && itm.uom.trim()) || "UN";
      // Asegurar descuento numérico
      itm.discount = parseFloat(itm.discount || itm.descuento || 0) || 0;
      // Normalizar tasa impuesto (cadena -> número). No forzar 19 si ya trae valor (incluye 0 o 8)
      const originalTax = itm.tax !== undefined ? itm.tax : itm.taxes;
      let parsedTax =
        originalTax === undefined || originalTax === null || originalTax === ""
          ? undefined
          : parseFloat(originalTax);
      if (isNaN(parsedTax)) parsedTax = undefined;
      if (parsedTax === undefined) {
        parsedTax = 19; // fallback
        itm._taxSource = "default-19";
      } else {
        itm._taxSource = "source";
      }
      itm.tax = parsedTax; // guardamos como número

      // Si trae ean vacío generamos uno
      if (!itm.ean) itm.ean = `M-${Date.now()}`;

      // Calcular base/impuesto unitarios si no vienen o si están inconsistentes
      const epsilon = 0.02; // tolerancia redondeo
      const alreadyHasUnit = itm.price_amount !== undefined && itm.item_tax !== undefined;

      if (alreadyHasUnit) {
        // Si ya vienen los valores, usarlos directamente
        // NO recalcular basándonos en el precio bruto porque puede causar inconsistencias
        itm.price_amount = +parseFloat(itm.price_amount).toFixed(2);
        itm.item_tax = +parseFloat(itm.item_tax).toFixed(2);

        // Verificar consistencia pero solo para logging
        const recomposed = itm.price_amount + itm.item_tax;
        if (Math.abs(recomposed - itm.precio) > epsilon) {
          console.warn(
            "[POS] Valores inconsistentes detectados pero usando los originales:",
            {
              price_amount: itm.price_amount,
              item_tax: itm.item_tax,
              suma: recomposed,
              precio: itm.precio,
              diferencia: recomposed - itm.precio,
            }
          );
        }
        itm._recalc = false;
      } else {
        // Solo si NO vienen los valores, calcularlos
        const { baseUnit, taxUnit } = this.computeItemAmounts(itm);
        itm.price_amount = +baseUnit.toFixed(2);
        itm.item_tax = +taxUnit.toFixed(2);
        itm._recalc = true;
      }
      // Totales de línea derivados (para inspección)
      itm.line_base_total = +(itm.price_amount * itm.cantidad).toFixed(2);
      itm.line_tax_total = +(itm.item_tax * itm.cantidad).toFixed(2);
      itm.line_gross_total = +(itm.precio * itm.cantidad).toFixed(2);

      console.log("[POS] add2cart -> RAW", rawSnapshot);
      console.log("[POS] add2cart -> NORMALIZED", {
        ean: itm.ean,
        nombre: itm.nombre,
        uom: itm.uom,
        cantidad: itm.cantidad,
        precio_bruto: itm.precio,
        tax: itm.tax,
        tax_source: itm._taxSource,
        price_amount_unit: itm.price_amount,
        item_tax_unit: itm.item_tax,
        line_base_total: itm.line_base_total,
        line_tax_total: itm.line_tax_total,
        recomputed: itm._recalc,
      });

      const idx = this.cart.findIndex((p) => p.ean === itm.ean);
      if (idx === -1) {
        this.cart.push(itm);
      } else {
        this.cart[idx].cantidad += quantity;
        // actualizar totales de línea para el existente
        this.cart[idx].line_base_total = +(
          this.cart[idx].price_amount * this.cart[idx].cantidad
        ).toFixed(2);
        this.cart[idx].line_tax_total = +(
          this.cart[idx].item_tax * this.cart[idx].cantidad
        ).toFixed(2);
        this.cart[idx].line_gross_total = +(
          this.cart[idx].precio * this.cart[idx].cantidad
        ).toFixed(2);
        console.log("[POS] add2cart -> INCREMENT", {
          ean: this.cart[idx].ean,
          nombre: this.cart[idx].nombre,
          uom: this.cart[idx].uom,
          nueva_cantidad: this.cart[idx].cantidad,
          line_base_total: this.cart[idx].line_base_total,
          line_tax_total: this.cart[idx].line_tax_total,
        });
      }
      console.log("[POS] add2cart -> CART", this.cart);
      this.updateTotals();
      this.$nextTick(() => {
        const container = this.$refs.cartContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    computeItemAmounts(item) {
      const taxRate = parseFloat(item.tax) || 0;
      const gross = parseFloat(item.precio) || 0;
      if (taxRate <= 0) return { baseUnit: gross, taxUnit: 0 };
      const baseUnit = +(gross / (1 + taxRate / 100)).toFixed(4); // mayor precisión
      const taxUnit = +(gross - baseUnit).toFixed(4);
      return { baseUnit, taxUnit };
    },
    changeQty(item, delta) {
      if (delta < 0 && item.cantidad <= 1) return;
      item.cantidad += delta;
      this.updateTotals();
    },
    removeItem(item) {
      const idx = this.cart.indexOf(item);
      if (idx > -1) this.cart.splice(idx, 1);
      this.updateTotals();
    },
    updateTotals() {
      this.totals.qty = 0;
      this.totals.total = 0;
      this.totals.paid = 0;
      this.cart.forEach((i) => {
        this.totals.qty += i.cantidad;
        this.totals.total += i.cantidad * i.precio;
      });
      this.payments.forEach((p) => {
        if (p.payment_code !== "00") this.totals.paid += parseFloat(p.payment_value);
      });
      this.totals.unpaid = this.totals.total - this.totals.paid;
      this.payment.payment_value = this.totals.unpaid;

      // ✅ PROCESAMIENTO AUTOMÁTICO: Cuando el pago está completo, procesar factura FE
      if (this.totals.unpaid === 0 && this.totals.total > 0 && !this.processing) {
        console.log("✅ Pago completo, procesando factura FE automáticamente...");
        this.processInvoice();
      }
    },
    fastPayment() {
      if (this.totals.unpaid <= 0) return;
      this.payments.push({
        payment_concept: "EFECTIVO",
        payment_code: "01",
        payment_reference: "PAGO RAPIDO",
        payment_value: this.totals.unpaid,
      });
      this.updateTotals();
    },
    processPayment(paymentData) {
      console.log("=== RECIBIENDO PAGO EN INVOICEPOS ===");
      console.log("paymentData recibido:", paymentData);

      const { payment, pay_mode, vendor, client, doctype } = paymentData;

      // Validar datos recibidos
      if (!payment || !pay_mode || !vendor) {
        console.error("❌ Datos de pago incompletos:", { payment, pay_mode, vendor });
        this.showMessage({
          text: "Error: Datos de pago incompletos",
          color: "error",
          timeout: 3000,
        });
        return;
      }

      // Validar valor del pago
      if (!payment.payment_value || payment.payment_value <= 0) {
        console.error("❌ Valor de pago inválido:", payment.payment_value);
        this.showMessage({
          text: "Error: Valor de pago inválido",
          color: "error",
          timeout: 3000,
        });
        return;
      }

      console.log("✅ Datos validados, procesando pago...");

      // Actualizar datos del componente
      this.vendor = vendor;
      this.documentType = doctype || this.documentType;
      if (client) {
        this.client = { ...client };
      }

      // Ajustar efectivo con cambio
      if (pay_mode.codigo === "01" && payment.payment_value > this.totals.unpaid) {
        const change = payment.payment_value - this.totals.unpaid;
        this.payments.push({
          payment_concept: pay_mode.valor,
          payment_code: pay_mode.codigo,
          payment_reference: "EFECTIVO",
          payment_value: this.totals.unpaid,
        });
        this.payments.push({
          payment_concept: "CAMBIO",
          payment_code: "01",
          payment_reference: `Cambio ${this.formatCurrency(change)}`,
          payment_value: 0,
        });
        console.log("✅ Pago en efectivo con cambio procesado");
      } else if (payment.payment_value <= this.totals.unpaid) {
        this.payments.push({
          payment_concept: pay_mode.valor,
          payment_code: pay_mode.codigo,
          payment_reference: payment.payment_reference || pay_mode.valor,
          payment_value: payment.payment_value,
        });
        console.log("✅ Pago procesado:", payment.payment_value);
      } else {
        console.error("❌ Valor excede el monto pendiente");
        this.showMessage({
          text: "Error: El valor excede el monto pendiente",
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.updateTotals();

      // Mostrar mensaje de confirmación
      this.showMessage({
        text: `Pago registrado: ${this.formatCurrency(payment.payment_value)}`,
        color: "success",
        timeout: 2000,
      });

      console.log("💰 Totales actualizados:", this.totals);
    },
    openCheckout() {
      if (this.cart.length === 0) return;
      this.resetValidationStatus(); // NUEVO
      this.checkoutDialog = true;
    },
    resetValidationStatus() {
      // NUEVO: resetear todas las validaciones
      this.checkoutValidations.forEach((v) => {
        if (v.key === "email" || v.key === "electronic" || v.key === "value") {
          // Estos campos son opcionales o tienen lógica especial
          v.status = v.key === "email" ? true : false;
        } else {
          // Los demás se ponen en null para indicar que necesitan validación
          v.status = null;
        }
      });
    },
    reset() {
      this.payment = createPayment();
      this.cart = [];
      this.payments = [];
      this.updateTotals();
      this.client = { ...this.clientesGenericos.consumidor };
    },

    processInvoice() {
      if (!this.canProcess) return;
      this.processing = true;

      // Procesar items con campos calculados
      const processedItems = this.cart.map((item) => {
        const { baseUnit, taxUnit } = this.computeItemAmounts(item);
        return {
          ...item,
          qty: item.cantidad,
          price_amount: +baseUnit.toFixed(2),
          item_tax: +taxUnit.toFixed(2),
          discount: item.discount || 0,
        };
      });

      // Agregar typeid al cliente si tiene identificación
      const processedBuyer = {
        ...this.client,
        typeid: this.client.typeid ? "CC" : undefined,
      };

      const invoice = {
        buyer: processedBuyer,
        doc: {
          doc_type: "FE",
          doc_value: this.totals.total,
          store: window.store.store_id,
          userId: window.profile.user_email,
          vendor: this.vendor,
        },
        items: processedItems,
        payments: this.payments,
        seller: window.seller,
        store: window.store.store_id,
      };
      console.log("🌐 Enviando factura al servidor:", invoice);
      webserver("post_invoice", invoice, (data) => {
        console.log("✅ Factura procesada:", data);
        this.processing = false;
        this.item = { ...data };

        this.dialog = true;

        this.showMessage({ text: "Factura procesada", color: "success" });
        this.reset();
      });
    },
    saveHold() {
      // Placeholder: aquí se podría llamar a invoice_save o endpoint nuevo
      this.showMessage({ text: "Cuenta retenida (placeholder)", color: "info" });
    },
    setConsumidorFinal() {
      this.client = {
        ...this.clientesGenericos.consumidor,
        CityName: this.company.city_code || this.client.CityName,
      };
    },
    setClienteContado() {
      this.client = {
        ...this.clientesGenericos.contado,
        CityName: this.company.city_code || this.client.CityName,
      };
    },
    openClientDialog() {
      this.showMessage({
        text: "Implementar diálogo cliente (placeholder)",
        color: "info",
      });
    },
  },
};
</script>

<style scoped>
.v-toolbar {
  flex: none;
}

/* CONTENEDOR TOTAL: alto fijo del viewport, no scrollea ni se sale */
.pos-root {
  height: calc(100vh - 48px) !important;       /* app-bar dense = 48px */
  max-height: calc(100vh - 48px) !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;                  /* ningún scroll en el total */
  background-color: #f5f5f5;
  box-sizing: border-box;
}

/* ROW: ocupa todo el alto disponible dentro del contenedor total */
.pos-row {
  flex: 1 1 0 !important;        /* basis 0 → tamaño basado en grow, no en contenido */
  min-height: 0 !important;
  display: flex !important;
  flex-direction: row !important;
  overflow: hidden !important;   /* las columnas no pueden empujar el row */
  gap: 8px;
  margin: 0 !important;
}

/* COL: cada una es flex-column con altura definida por el row */
.pos-col {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  min-width: 0;
  overflow: hidden !important;   /* las cards internas no pueden empujar el col */
  flex: 1 1 0;
}

/* Estilos para la sección del carrito */
.cart-col {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* CARRITO: card que llena el col, contenido scrollea internamente */
.cart-section {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  background: white;
  border-radius: 8px !important;
  overflow: hidden !important;
}

.cart-header {
  border-radius: 8px 8px 0 0 !important;
}

.client-info-card {
  border-radius: 6px;
  background: #f9fafb;
  transition: all 0.3s ease;
}

.client-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cart-table-wrapper {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

/* Único punto de scroll en el carrito */
.cart-items-container {
  border-radius: 6px;
  background: white;
  flex: 1 1 0 !important;
  overflow-y: auto !important;
  min-height: 0 !important;
}

.cart-list {
  background: transparent;
}

.cart-item-row {
  transition: background-color 0.2s ease;
  border-radius: 4px;
  padding: 8px 4px;
}

.cart-item-row .v-list-item__action {
  margin: 0;
  align-self: flex-start !important;
}

.cart-item-row .v-list-item__content {
  padding: 4px 0;
}

.totals-card {
  flex: 0 0 auto;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

/* PANEL DE PRODUCTOS: card que llena el col, grid scrollea internamente */
.product-section {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
  background: white;
  border-radius: 8px !important;
  overflow: hidden !important;
}

.product-panel {
  flex: 1 1 0 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.shrink-tabs {
  flex: 0 0 auto;
}

.pad-items {
  flex: 1 1 0 !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

/* Vuetify inserta .v-window__container entre v-window y v-window-item:
   lo forzamos a propagar la altura disponible */
.pad-items::v-deep > .v-window__container {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  overflow: hidden !important;
}

.pad-item-wrapper {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  padding: 8px;
}

.pad-item-wrapper > * {
  flex: 1 1 0 !important;
  min-height: 0 !important;
}

.shrink-footer {
  flex: 0 0 auto;
}

/* Mejorar visualización en dispositivos móviles */
@media (max-width: 960px) {
  .pos-row {
    flex-direction: column;
  }

  .cart-section,
  .product-section {
    margin-bottom: 16px;
  }
}

/* Animaciones suaves */
.cart-section,
.product-section {
  transition: box-shadow 0.3s ease;
}

.cart-section:hover,
.product-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Estilos del diálogo de cantidad */
.qty-display >>> .v-text-field__slot input {
  text-align: center;
  font-size: 2.5rem !important;
  font-weight: bold;
  color: #1976d2;
}

.qty-display {
  margin-bottom: 16px;
}
</style>
